/**
 * Legal Documents and Dispute Services Content
 */

// ==================== LEGAL DOCUMENTS ====================

export const legalNotice = {
  id: "legal-notice",
  slug: "legal-notice",
  seoTitle: "Legal Notice Drafting India | Send Legal Notice Online",
  seoDescription: "Professional legal notice drafting and delivery services. Recovery notices, cheque bounce notices, breach of contract notices. Expert lawyers draft compelling legal notices.",
  heroTitle: "Legal Notice Drafting",
  heroSubtitle: "Legal Documents",
  summary: "A Legal Notice is a formal communication sent to a person or entity informing them of your intention to undertake legal proceedings against them. It gives the recipient an opportunity to settle the matter before court action. Our expert lawyers draft compelling, legally sound notices for various situations including money recovery, property disputes, breach of contract, and more.",
  
  whoNeeds: {
    title: "Who Needs Legal Notice Services?",
    description: "Anyone facing a dispute that may require legal action should first send a legal notice.",
    points: [
      "Creditors seeking to recover money or dues",
      "Cheque bounce victims (Section 138 NI Act)",
      "Landlords dealing with non-paying tenants",
      "Employees with unpaid salary or benefits",
      "Parties to breached contracts or agreements",
      "Property owners facing encroachment or disputes",
      "Consumers with grievances against businesses",
      "Defamation or harassment victims"
    ]
  },
  
  whyRequired: {
    title: "Why is Legal Notice Important?",
    description: "Legal notice is often mandatory before filing cases and demonstrates seriousness of intent.",
    points: [
      "Mandatory Requirement: Required before filing cases under Section 138 NI Act and certain civil suits.",
      "Settlement Opportunity: Often leads to settlement without expensive court proceedings.",
      "Evidence: Creates documented record of your grievance and demand.",
      "Limitation Period: Protects your rights by documenting claim within limitation period.",
      "Psychological Pressure: Formal legal notice often prompts quick response.",
      "Court Recognition: Shows court that you attempted resolution before litigation."
    ]
  },
  
  benefits: [
    { title: "Expert Drafting", description: "Notices drafted by experienced lawyers with proper legal language." },
    { title: "Quick Settlement", description: "Many disputes settle after receiving well-drafted legal notice." },
    { title: "Cost Effective", description: "Much cheaper than court proceedings - try resolution first." },
    { title: "Legal Compliance", description: "Notices comply with legal requirements for court admissibility." },
    { title: "Documentation", description: "Creates permanent record of your claim and demands." },
    { title: "Delivery Proof", description: "Registered AD/Speed Post with acknowledgment for court." },
    { title: "Follow-up", description: "We track responses and advise on next steps." },
    { title: "Multiple Formats", description: "Standard, detailed, or reply notices as needed." }
  ],
  
  documentsRequired: {
    title: "Information for Legal Notice",
    description: "Details needed to draft effective legal notice.",
    directors: [
      "Your complete name and address",
      "Identity proof",
      "Contact details",
      "Signature"
    ],
    company: [
      "Opposite party details (name, address)",
      "Description of dispute/issue",
      "Supporting documents (agreements, invoices, cheques)",
      "Previous communication records",
      "Amount/relief being claimed",
      "Timeline of events",
      "Any legal history of the matter"
    ]
  },
  
  process: [
    { step: 1, title: "Case Understanding", description: "Discuss your case and understand facts, documents, and desired outcome.", duration: "Same day" },
    { step: 2, title: "Document Review", description: "Review all supporting documents and evidence.", duration: "1 day" },
    { step: 3, title: "Notice Drafting", description: "Our lawyers draft comprehensive legal notice.", duration: "1-2 days" },
    { step: 4, title: "Client Review", description: "Share draft for your review and incorporate feedback.", duration: "1 day" },
    { step: 5, title: "Finalization", description: "Finalize notice with your signature.", duration: "Same day" },
    { step: 6, title: "Dispatch", description: "Send via Registered AD/Speed Post with tracking.", duration: "1 day" }
  ],
  
  timeline: {
    total: "3-5 business days",
    breakdown: [
      { phase: "Consultation & document review", duration: "1-2 days" },
      { phase: "Drafting", duration: "1-2 days" },
      { phase: "Review & dispatch", duration: "1-2 days" }
    ],
    note: "Express service available for urgent matters (24-48 hours)."
  },
  
  pricing: {
    title: "Legal Notice Pricing",
    description: "Professional legal notice services.",
    packages: [
      {
        name: "Standard Notice",
        price: "₹2,999",
        description: "Basic legal notice",
        features: [
          "Lawyer consultation",
          "Standard format notice",
          "One round revision",
          "Registered post dispatch",
          "Delivery tracking"
        ]
      },
      {
        name: "Detailed Notice",
        price: "₹5,999",
        description: "Comprehensive notice",
        features: [
          "Detailed case analysis",
          "Comprehensive drafting",
          "Multiple revisions",
          "Speed post + courier",
          "Response handling advice"
        ],
        popular: true
      },
      {
        name: "Complex/Reply",
        price: "₹9,999",
        description: "Complex matters or reply",
        features: [
          "Senior lawyer drafting",
          "Detailed legal research",
          "Reply to notice received",
          "Rejoinder if needed",
          "Court case readiness"
        ]
      }
    ],
    governmentFees: "Postal charges: ₹100-300 for registered AD/Speed Post."
  },
  
  faqs: [
    { question: "Is legal notice mandatory before filing court case?", answer: "For certain cases like cheque bounce (Section 138), legal notice is mandatory. For civil suits, it's highly recommended but not always mandatory." },
    { question: "What is the reply period for legal notice?", answer: "Typically 15-30 days depending on the matter. For cheque bounce cases, 15 days from receipt is standard. The notice should specify reply period." },
    { question: "Can I send legal notice by email?", answer: "Email alone may not be legally sufficient. Send via Registered Post/Speed Post for proof of delivery. Email can be additional communication." },
    { question: "What if the other party doesn't respond?", answer: "Non-response within stipulated time gives you ground to proceed with legal action. Keep the postal receipt and AD card as evidence." },
    { question: "Can I draft legal notice myself?", answer: "Yes, but professionally drafted notices are more effective, legally sound, and carry more weight. Improper notices can harm your case." },
    { question: "What happens after sending notice?", answer: "Wait for response within stipulated period. They may settle, deny claims, or not respond. Based on response, decide on settlement talks or court proceedings." }
  ],
  
  relatedServices: [
    { id: "agreements-drafting", label: "Agreement Drafting" },
    { id: "consumer-complaint", label: "Consumer Complaint" },
    { id: "cheque-bounce", label: "Cheque Bounce Case" },
    { id: "arbitration", label: "Arbitration" },
    { id: "contract-review", label: "Contract Review" },
    { id: "legal-consultation", label: "Legal Consultation" }
  ],
  
  trustSignals: {
    incorporations: "15,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Expert Lawyers"
  }
};

export const agreementsDrafting = {
  id: "agreements-drafting",
  slug: "agreements-drafting",
  seoTitle: "Agreement Drafting Services | Legal Contract Drafting India",
  seoDescription: "Professional agreement and contract drafting services. Business agreements, employment contracts, service agreements, lease deeds. Expert lawyers ensure legally binding documents.",
  heroTitle: "Agreement & Contract Drafting",
  heroSubtitle: "Legal Documents",
  summary: "Professionally drafted agreements and contracts are essential for protecting your business interests and avoiding disputes. Our experienced lawyers draft comprehensive, legally enforceable agreements tailored to your specific requirements. From business partnerships to employment contracts, we ensure your agreements cover all necessary clauses and comply with applicable laws.",
  
  whoNeeds: {
    title: "Who Needs Agreement Drafting?",
    description: "Anyone entering into formal business or personal arrangements should have proper agreements.",
    points: [
      "Businesses entering partnerships or joint ventures",
      "Employers hiring employees or consultants",
      "Landlords and tenants for rental agreements",
      "Service providers and clients",
      "Vendors and suppliers for supply agreements",
      "Startups with co-founders or investors",
      "Freelancers and contractors",
      "Anyone buying or selling assets/business"
    ]
  },
  
  whyRequired: {
    title: "Why Professional Drafting Matters?",
    description: "Proper agreements prevent disputes and protect your legal rights.",
    points: [
      "Legal Enforceability: Properly drafted agreements are enforceable in court.",
      "Dispute Prevention: Clear terms prevent misunderstandings and disputes.",
      "Risk Allocation: Properly allocate risks and liabilities between parties.",
      "Compliance: Ensure compliance with applicable laws and regulations.",
      "Protection: Protect intellectual property, confidential information, and business interests.",
      "Exit Clarity: Clear termination and exit provisions avoid messy separations."
    ]
  },
  
  benefits: [
    { title: "Custom Drafting", description: "Agreements tailored to your specific business needs and situation." },
    { title: "Legal Expertise", description: "Drafted by experienced lawyers with industry knowledge." },
    { title: "Comprehensive", description: "All necessary clauses including boilerplate and specific terms." },
    { title: "Legally Sound", description: "Compliant with applicable laws and enforceable in court." },
    { title: "Risk Protection", description: "Proper indemnity, liability, and termination clauses." },
    { title: "Quick Turnaround", description: "Fast drafting without compromising on quality." },
    { title: "Revisions", description: "Multiple rounds of revisions based on your feedback." },
    { title: "Stamp & Registration", description: "Guidance on stamp duty and registration requirements." }
  ],
  
  documentsRequired: {
    title: "Information for Agreement Drafting",
    description: "Basic information needed to draft your agreement.",
    directors: [
      "Party names and addresses",
      "Identity proofs",
      "Contact details",
      "Authorized signatory details"
    ],
    company: [
      "Nature of agreement/arrangement",
      "Key commercial terms discussed",
      "Duration and renewal terms",
      "Payment terms",
      "Specific obligations of each party",
      "Any existing agreement (if amendment)"
    ]
  },
  
  process: [
    { step: 1, title: "Requirement Discussion", description: "Understand the arrangement, parties, and key terms.", duration: "30-60 mins" },
    { step: 2, title: "Information Collection", description: "Gather party details and commercial terms.", duration: "1 day" },
    { step: 3, title: "First Draft", description: "Prepare comprehensive first draft of agreement.", duration: "2-3 days" },
    { step: 4, title: "Review & Feedback", description: "Share draft and incorporate your feedback.", duration: "1-2 rounds" },
    { step: 5, title: "Finalization", description: "Finalize agreement after all revisions.", duration: "1 day" },
    { step: 6, title: "Execution", description: "Guide on stamp duty, signing, and registration if needed.", duration: "As applicable" }
  ],
  
  timeline: {
    total: "5-7 business days",
    breakdown: [
      { phase: "Requirement gathering", duration: "1 day" },
      { phase: "Drafting", duration: "2-3 days" },
      { phase: "Revisions & finalization", duration: "2-3 days" }
    ],
    note: "Express service available for urgent requirements (2-3 days)."
  },
  
  pricing: {
    title: "Agreement Drafting Fees",
    description: "Professional drafting for various agreement types.",
    packages: [
      {
        name: "Standard Agreement",
        price: "₹5,999",
        description: "Common agreements",
        features: [
          "Service/employment/rental",
          "Standard clauses",
          "2 revision rounds",
          "Word + PDF formats",
          "Execution guidance"
        ]
      },
      {
        name: "Business Agreement",
        price: "₹11,999",
        description: "Complex agreements",
        features: [
          "Partnership/JV/shareholder",
          "Detailed negotiation support",
          "Unlimited revisions",
          "Stamp duty calculation",
          "Registration assistance"
        ],
        popular: true
      },
      {
        name: "Custom/Complex",
        price: "₹19,999+",
        description: "Highly specialized",
        features: [
          "M&A/investment agreements",
          "Cross-border contracts",
          "Industry-specific terms",
          "Senior lawyer handling",
          "Negotiation support"
        ]
      }
    ],
    governmentFees: "Stamp duty varies by state and agreement value. Registration fees additional where required."
  },
  
  faqs: [
    { question: "What types of agreements do you draft?", answer: "We draft all types including: Service Agreements, Employment Contracts, Rental/Lease Agreements, Partnership Deeds, Shareholder Agreements, NDAs, Vendor Agreements, Franchise Agreements, and more." },
    { question: "Is stamp duty required on agreements?", answer: "Many agreements require stamp duty based on state laws and agreement value. Unstamped agreements may not be admissible as evidence. We advise on applicable stamp duty." },
    { question: "Do agreements need to be registered?", answer: "Certain agreements like long-term leases, sale deeds, and partnership deeds require registration. We guide on registration requirements." },
    { question: "Can you review and modify existing agreements?", answer: "Yes, we review existing agreements, identify issues, and draft amendments or new versions as needed." },
    { question: "How do you handle confidential information?", answer: "All information shared is treated confidentially. We can sign NDAs before engagement if required." },
    { question: "Can agreements be executed digitally?", answer: "Yes, many agreements can be executed with digital/electronic signatures. Some agreements still require physical signatures and stamp paper." }
  ],
  
  relatedServices: [
    { id: "nda-agreement", label: "NDA Drafting" },
    { id: "legal-notice", label: "Legal Notice" },
    { id: "contract-review", label: "Contract Review" },
    { id: "company-registration", label: "Company Registration" },
    { id: "partnership-firm", label: "Partnership Deed" },
    { id: "power-of-attorney", label: "Power of Attorney" }
  ],
  
  trustSignals: {
    incorporations: "20,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert Lawyers"
  }
};

export const ndaAgreement = {
  id: "nda-agreement",
  slug: "nda-agreement",
  seoTitle: "NDA Drafting India | Non-Disclosure Agreement Template",
  seoDescription: "Professional NDA and confidentiality agreement drafting. Protect your business secrets with legally binding non-disclosure agreements. Expert lawyer services.",
  heroTitle: "Non-Disclosure Agreement (NDA)",
  heroSubtitle: "Legal Documents",
  summary: "A Non-Disclosure Agreement (NDA) is a legally binding contract that establishes confidentiality between parties. It protects sensitive business information, trade secrets, and proprietary data from being disclosed to unauthorized parties. Our lawyers draft robust NDAs customized to your specific confidentiality requirements.",
  
  whoNeeds: {
    title: "Who Needs NDA?",
    description: "Anyone sharing confidential information should protect it with an NDA.",
    points: [
      "Startups sharing ideas with investors or partners",
      "Businesses hiring employees with access to sensitive data",
      "Companies engaging vendors or consultants",
      "Parties exploring potential partnerships or M&A",
      "Technology companies sharing proprietary information",
      "Businesses sharing customer data or business processes",
      "Franchisors sharing business systems with franchisees",
      "Anyone sharing trade secrets or intellectual property"
    ]
  },
  
  whyRequired: {
    title: "Why is NDA Important?",
    description: "NDAs provide legal protection for your confidential information.",
    points: [
      "Legal Protection: Creates legally enforceable obligation of confidentiality.",
      "Trade Secret Protection: Essential for maintaining trade secret status.",
      "Deterrent: Deters parties from misusing confidential information.",
      "Remedies: Enables legal action and damages for breach.",
      "Due Diligence: Standard requirement in business transactions.",
      "Employee Relations: Protects company information when employees leave."
    ]
  },
  
  benefits: [
    { title: "Confidentiality", description: "Legally binding obligation to keep information confidential." },
    { title: "Trade Secrets", description: "Maintain trade secret protection under IP laws." },
    { title: "Legal Recourse", description: "Sue for damages and injunctions if breached." },
    { title: "Professional Trust", description: "Demonstrates seriousness about protecting information." },
    { title: "Customized Terms", description: "NDAs tailored to your specific confidentiality needs." },
    { title: "Duration Control", description: "Set appropriate confidentiality periods." },
    { title: "Scope Definition", description: "Clearly define what information is confidential." },
    { title: "Quick Execution", description: "Standard NDAs can be ready in 24-48 hours." }
  ],
  
  documentsRequired: {
    title: "Information for NDA Drafting",
    description: "Basic details needed for NDA preparation.",
    directors: [
      "Disclosing party details",
      "Receiving party details",
      "Authorized signatory information"
    ],
    company: [
      "Purpose of disclosure",
      "Description of confidential information",
      "Duration of confidentiality obligation",
      "Any exceptions to confidentiality",
      "Permitted use of information",
      "Geographic scope (if relevant)",
      "Special requirements"
    ]
  },
  
  process: [
    { step: 1, title: "Requirement Understanding", description: "Understand what information needs protection and the context.", duration: "30 mins" },
    { step: 2, title: "NDA Type Selection", description: "Determine unilateral, bilateral, or multilateral NDA.", duration: "Immediate" },
    { step: 3, title: "Drafting", description: "Draft customized NDA with appropriate clauses.", duration: "1-2 days" },
    { step: 4, title: "Review", description: "Share draft and incorporate feedback.", duration: "1 day" },
    { step: 5, title: "Finalization", description: "Finalize NDA after revisions.", duration: "Same day" },
    { step: 6, title: "Execution Guidance", description: "Guide on proper execution and storage.", duration: "Immediate" }
  ],
  
  timeline: {
    total: "2-4 business days",
    breakdown: [
      { phase: "Requirement gathering", duration: "Same day" },
      { phase: "Drafting", duration: "1-2 days" },
      { phase: "Revisions & finalization", duration: "1 day" }
    ],
    note: "Express service available for standard NDAs (24 hours)."
  },
  
  pricing: {
    title: "NDA Drafting Fees",
    description: "Professional NDA drafting services.",
    packages: [
      {
        name: "Standard NDA",
        price: "₹2,999",
        description: "Unilateral NDA",
        features: [
          "One-way confidentiality",
          "Standard terms",
          "1 revision round",
          "Word + PDF",
          "Execution guidance"
        ]
      },
      {
        name: "Mutual NDA",
        price: "₹4,999",
        description: "Bilateral NDA",
        features: [
          "Two-way confidentiality",
          "Customized terms",
          "2 revision rounds",
          "Specific carve-outs",
          "Negotiation support"
        ],
        popular: true
      },
      {
        name: "Complex NDA",
        price: "₹8,999",
        description: "Specialized NDA",
        features: [
          "Multi-party NDA",
          "Technical annexures",
          "Industry-specific terms",
          "Unlimited revisions",
          "Complete support"
        ]
      }
    ],
    governmentFees: "NDAs typically don't require stamp duty or registration. May vary by state."
  },
  
  faqs: [
    { question: "What is the difference between unilateral and mutual NDA?", answer: "Unilateral NDA protects one party's information (e.g., employer-employee). Mutual NDA protects both parties when both share confidential information (e.g., business discussions)." },
    { question: "How long should NDA confidentiality last?", answer: "Typically 2-5 years after disclosure. For trade secrets, confidentiality should last as long as information remains secret. We advise based on your situation." },
    { question: "What happens if NDA is breached?", answer: "You can seek damages, injunction to stop further disclosure, and other legal remedies. Well-drafted NDAs specify consequences of breach." },
    { question: "Can NDA be enforced across countries?", answer: "Cross-border NDAs need specific provisions for jurisdiction, governing law, and enforcement. We can draft international NDAs with appropriate terms." },
    { question: "What should be excluded from NDA?", answer: "Common exclusions: publicly available information, information already known to recipient, information independently developed, and information disclosed by third parties legally." },
    { question: "Is digital signature valid for NDA?", answer: "Yes, electronically signed NDAs are legally valid under IT Act. However, some situations may warrant physical signatures." }
  ],
  
  relatedServices: [
    { id: "agreements-drafting", label: "Agreement Drafting" },
    { id: "employment-agreement", label: "Employment Agreement" },
    { id: "trademark-registration", label: "Trademark Registration" },
    { id: "copyright-registration", label: "Copyright Registration" },
    { id: "contract-review", label: "Contract Review" },
    { id: "startup-legal", label: "Startup Legal" }
  ],
  
  trustSignals: {
    incorporations: "25,000+",
    rating: "4.9/5",
    experience: "10+ Years",
    support: "Expert Lawyers"
  }
};

// ==================== DISPUTE RESOLUTION ====================

export const consumerComplaint = {
  id: "consumer-complaint",
  slug: "consumer-complaint",
  seoTitle: "Consumer Complaint Filing | Consumer Forum Case India",
  seoDescription: "File consumer complaints against defective products and deficient services. Expert representation at District, State, and National Consumer Forums. Quick dispute resolution.",
  heroTitle: "Consumer Complaint Filing",
  heroSubtitle: "Dispute Resolution",
  summary: "If you've received defective products, deficient services, or faced unfair trade practices, you have the right to file a consumer complaint. The Consumer Protection Act, 2019 provides for quick and effective dispute resolution through Consumer Forums. Our experts help you file complaints and represent you throughout the process for maximum compensation.",
  
  whoNeeds: {
    title: "Who Can File Consumer Complaints?",
    description: "Any consumer who has suffered loss due to defective products or deficient services.",
    points: [
      "Buyers of defective products (electronics, vehicles, appliances)",
      "Recipients of deficient services (insurance, banking, telecom)",
      "Victims of unfair trade practices or misleading ads",
      "Patients affected by medical negligence",
      "Customers charged excessive prices",
      "Builders/developers defaulting on delivery",
      "Service recipients facing harassment or misconduct",
      "Anyone denied rightful services or refunds"
    ]
  },
  
  whyRequired: {
    title: "Why File Consumer Complaint?",
    description: "Consumer forums provide quick, affordable justice without lawyers.",
    points: [
      "Quick Resolution: Consumer cases are typically resolved within 90-150 days.",
      "Affordable: Nominal filing fees and no mandatory lawyer requirement.",
      "Compensation: Get compensation for financial loss, mental agony, and harassment.",
      "Punitive Damages: Forums can award punitive damages for willful negligence.",
      "Refund/Replacement: Get defective products replaced or refunded.",
      "Accessible: File online or offline at convenient locations."
    ]
  },
  
  benefits: [
    { title: "Expert Assessment", description: "We evaluate your case and advise on strength and potential compensation." },
    { title: "Complete Filing", description: "Draft and file comprehensive complaint with all supporting evidence." },
    { title: "Forum Selection", description: "File at appropriate forum (District/State/National) based on claim value." },
    { title: "Representation", description: "Represent you at hearings and arguments." },
    { title: "Documentation", description: "Organize evidence and documents for maximum impact." },
    { title: "Execution", description: "Help execute orders and recover compensation." },
    { title: "Appeals", description: "Handle appeals if initial order is unfavorable." },
    { title: "Quick Resolution", description: "Push for expedited hearing and resolution." }
  ],
  
  documentsRequired: {
    title: "Documents for Consumer Complaint",
    description: "Evidence and documents to support your complaint.",
    directors: [
      "Consumer's identity proof (Aadhaar/PAN)",
      "Address proof",
      "Contact details",
      "Photographs (if applicable)"
    ],
    company: [
      "Purchase invoice/receipt/bill",
      "Product warranty card",
      "Defect evidence (photos, videos)",
      "Service agreement/terms",
      "Communication with company",
      "Complaint records (if any)",
      "Loss calculation and evidence",
      "Similar complaint references (if known)"
    ]
  },
  
  process: [
    { step: 1, title: "Case Evaluation", description: "Analyze your case, evidence, and potential relief.", duration: "1 day" },
    { step: 2, title: "Notice to Opposite Party", description: "Send legal notice seeking resolution (optional but recommended).", duration: "15 days" },
    { step: 3, title: "Complaint Drafting", description: "Draft comprehensive complaint with facts, evidence, and relief sought.", duration: "2-3 days" },
    { step: 4, title: "Filing", description: "File complaint at appropriate consumer forum with fees.", duration: "1 day" },
    { step: 5, title: "Admission & Notice", description: "Forum admits complaint and issues notice to opposite party.", duration: "7-30 days" },
    { step: 6, title: "Hearing & Order", description: "Attend hearings, present arguments, and obtain order.", duration: "90-150 days" }
  ],
  
  timeline: {
    total: "3-6 months typically",
    breakdown: [
      { phase: "Filing & admission", duration: "7-30 days" },
      { phase: "Reply from opposite party", duration: "30-45 days" },
      { phase: "Arguments & order", duration: "60-90 days" }
    ],
    note: "Consumer forums are mandated to resolve cases within 90-150 days. Complex cases may take longer."
  },
  
  pricing: {
    title: "Consumer Complaint Services",
    description: "Filing and representation services based on claim value.",
    packages: [
      {
        name: "District Forum",
        price: "₹7,999",
        description: "Claims up to ₹1 crore",
        features: [
          "Case evaluation",
          "Complaint drafting",
          "Forum filing",
          "2 hearing representations",
          "Order tracking"
        ]
      },
      {
        name: "State Forum",
        price: "₹14,999",
        description: "Claims ₹1 crore - ₹10 crore",
        features: [
          "Everything in District",
          "Complex case handling",
          "Multiple hearings",
          "Execution assistance",
          "Appeal if needed"
        ],
        popular: true
      },
      {
        name: "National Forum",
        price: "₹24,999",
        description: "Claims above ₹10 crore",
        features: [
          "Everything in State",
          "Senior advocate",
          "Nationwide coverage",
          "All appeals included",
          "Complete support"
        ]
      }
    ],
    governmentFees: "Court fees: District ₹0-500, State ₹2,000-4,000, National ₹5,000-25,000 based on claim value."
  },
  
  faqs: [
    { question: "What is the limitation period for consumer complaints?", answer: "Generally 2 years from the date of cause of action (purchase or defect discovery). Delay can be condoned for sufficient cause." },
    { question: "Do I need a lawyer for consumer complaint?", answer: "No, consumer forums allow individuals to present their own cases. However, professional representation significantly improves outcomes." },
    { question: "Which forum should I file in?", answer: "District Forum: Claims up to ₹1 crore. State Commission: ₹1 crore to ₹10 crore. National Commission: Above ₹10 crore." },
    { question: "Can I file online?", answer: "Yes, consumer complaints can be filed online through e-daakhil portal (edaakhil.nic.in). We can assist with online filing." },
    { question: "What compensation can I get?", answer: "You can get: product replacement/refund, service fee refund, compensation for loss/injury, compensation for mental agony, litigation costs, and punitive damages." },
    { question: "Can companies appeal against consumer orders?", answer: "Yes, either party can appeal. District orders appealable to State Commission within 45 days. State orders to National Commission within 30 days." }
  ],
  
  relatedServices: [
    { id: "legal-notice", label: "Legal Notice" },
    { id: "arbitration", label: "Arbitration" },
    { id: "civil-litigation", label: "Civil Litigation" },
    { id: "insurance-claim", label: "Insurance Claim" },
    { id: "real-estate-dispute", label: "Property Dispute" },
    { id: "defamation-case", label: "Defamation" }
  ],
  
  trustSignals: {
    incorporations: "10,000+",
    rating: "4.8/5",
    experience: "10+ Years",
    support: "Consumer Lawyers"
  }
};

export const arbitration = {
  id: "arbitration",
  slug: "arbitration",
  seoTitle: "Arbitration Services India | Commercial Dispute Resolution",
  seoDescription: "Professional arbitration services for commercial disputes. Faster, confidential resolution without court delays. Expert arbitrators and representation.",
  heroTitle: "Arbitration Services",
  heroSubtitle: "Dispute Resolution",
  summary: "Arbitration is an alternative dispute resolution mechanism where disputes are resolved by neutral arbitrators instead of courts. It offers faster, confidential, and flexible resolution for commercial disputes. Our experts help draft arbitration clauses, initiate proceedings, represent parties, and ensure enforceable arbitral awards.",
  
  whoNeeds: {
    title: "Who Needs Arbitration Services?",
    description: "Parties seeking faster, confidential resolution of commercial disputes.",
    points: [
      "Businesses with disputes having arbitration clauses",
      "Parties seeking faster resolution than courts",
      "Commercial disputes requiring confidentiality",
      "International trade disputes",
      "Construction and infrastructure disputes",
      "Joint venture and partnership disputes",
      "Employment and HR-related disputes",
      "Insurance and banking disputes"
    ]
  },
  
  whyRequired: {
    title: "Why Choose Arbitration?",
    description: "Arbitration offers significant advantages over traditional litigation.",
    points: [
      "Speed: Typically resolved in 6-12 months vs years in court.",
      "Confidentiality: Proceedings are private unlike public court hearings.",
      "Expert Decision: Choose arbitrators with relevant expertise.",
      "Flexibility: Parties can agree on procedures and timelines.",
      "Finality: Awards are binding and have limited appeal grounds.",
      "Enforceability: Easier international enforcement under NY Convention."
    ]
  },
  
  benefits: [
    { title: "Expert Arbitrators", description: "Access to retired judges and domain experts as arbitrators." },
    { title: "Faster Resolution", description: "Complete dispute resolution typically within 12 months." },
    { title: "Confidentiality", description: "Private proceedings without public disclosure." },
    { title: "Flexibility", description: "Customize procedures to suit dispute complexity." },
    { title: "Binding Award", description: "Enforceable like court decree with limited appeals." },
    { title: "Cost Effective", description: "Often cheaper than prolonged court litigation." },
    { title: "Party Autonomy", description: "Choose rules, language, and seat of arbitration." },
    { title: "International Enforcement", description: "Awards enforceable in 160+ countries." }
  ],
  
  documentsRequired: {
    title: "Documents for Arbitration",
    description: "Documentation requirements for arbitration proceedings.",
    directors: [
      "Party identification documents",
      "Authorization letters",
      "Power of attorney for representative"
    ],
    company: [
      "Contract/agreement with arbitration clause",
      "Statement of claim or defense",
      "Supporting evidence and documents",
      "Communication records",
      "Financial records (if relevant)",
      "Expert reports (if needed)",
      "Previous correspondence on dispute"
    ]
  },
  
  process: [
    { step: 1, title: "Dispute Assessment", description: "Analyze dispute, arbitration clause, and strategy.", duration: "1-2 days" },
    { step: 2, title: "Arbitration Notice", description: "Send notice of arbitration to opposite party.", duration: "1 day" },
    { step: 3, title: "Arbitrator Appointment", description: "Appoint arbitrator(s) as per agreement/rules.", duration: "15-30 days" },
    { step: 4, title: "Statement of Claim", description: "File detailed claim with evidence and relief sought.", duration: "30 days" },
    { step: 5, title: "Hearings", description: "Attend hearings, examine witnesses, present arguments.", duration: "60-120 days" },
    { step: 6, title: "Award", description: "Arbitrator issues binding arbitral award.", duration: "30-60 days" }
  ],
  
  timeline: {
    total: "6-12 months",
    breakdown: [
      { phase: "Initiation & arbitrator appointment", duration: "1-2 months" },
      { phase: "Pleadings exchange", duration: "2-3 months" },
      { phase: "Hearings", duration: "2-4 months" },
      { phase: "Award", duration: "1-2 months" }
    ],
    note: "Timeline depends on dispute complexity and arbitration rules. Fast track options available."
  },
  
  pricing: {
    title: "Arbitration Service Fees",
    description: "Professional fees for arbitration representation.",
    packages: [
      {
        name: "Small Claims",
        price: "₹50,000",
        description: "Disputes up to ₹25 lakh",
        features: [
          "Case assessment",
          "Notice & initiation",
          "Claim/defense drafting",
          "Hearing representation",
          "Award execution"
        ]
      },
      {
        name: "Commercial",
        price: "₹1,50,000",
        description: "Disputes ₹25 lakh - ₹5 crore",
        features: [
          "Everything in Small Claims",
          "Expert witness coordination",
          "Detailed evidence analysis",
          "Multiple hearings",
          "Interim relief applications"
        ],
        popular: true
      },
      {
        name: "High Value",
        price: "Custom",
        description: "Disputes above ₹5 crore",
        features: [
          "Senior counsel handling",
          "Institutional arbitration",
          "International arbitration",
          "Comprehensive strategy",
          "Complete representation"
        ]
      }
    ],
    governmentFees: "Arbitrator fees vary based on dispute value and rules. Institutional fees additional for institutional arbitration."
  },
  
  faqs: [
    { question: "Is arbitration agreement mandatory?", answer: "Parties must have agreed to arbitration, either in the main contract or separately. Without agreement, parties must agree or court litigation is the option." },
    { question: "Can arbitration award be challenged?", answer: "Limited grounds for challenge under Section 34 (e.g., incapacity, invalid agreement, procedural irregularity, public policy). Merits cannot be challenged." },
    { question: "How is arbitrator appointed?", answer: "As per agreement - party-appointed, institutional appointment, or court appointment if parties can't agree. Sole arbitrator or 3-member tribunal." },
    { question: "Is arbitration confidential?", answer: "Generally yes, unless parties agree otherwise or court proceedings arise. Confidentiality is a key advantage over public court trials." },
    { question: "Can interim relief be sought?", answer: "Yes, parties can seek interim measures from arbitral tribunal or court before/during arbitration for preservation of evidence or assets." },
    { question: "What is institutional vs ad-hoc arbitration?", answer: "Institutional arbitration follows established rules (SIAC, ICC, MCIA) with administrative support. Ad-hoc is party-managed without institutional framework." }
  ],
  
  relatedServices: [
    { id: "legal-notice", label: "Legal Notice" },
    { id: "agreements-drafting", label: "Agreement Drafting" },
    { id: "commercial-litigation", label: "Commercial Litigation" },
    { id: "mediation", label: "Mediation" },
    { id: "contract-review", label: "Contract Review" },
    { id: "international-trade", label: "International Disputes" }
  ],
  
  trustSignals: {
    incorporations: "500+",
    rating: "4.9/5",
    experience: "15+ Years",
    support: "Arbitration Experts"
  }
};

// Export all services
export const legalDocumentServices = {
  legalNotice,
  agreementsDrafting,
  ndaAgreement
};

export const disputeServices = {
  consumerComplaint,
  arbitration
};
